import React, { useState, useEffect, useRef } from "react";
import { Table, Space, Card, Button, Select, Row, Col, message, Tag } from "antd";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { FileDownloadOutlined } from "@mui/icons-material";
import moment from "moment";
import Footer from "Common/Footer";
import ReportService from "Services/AdminServices/ReportServices";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { LeaveTypeEnum  , LeaveTypeEnumLabel} from "Constants/Enums/LeavePolicyEnum";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

import { Bar } from "react-chartjs-2";



ChartJS.register(ArcElement, Tooltip, Legend);


const { Option } = Select;

function EmployeeAttendanceReport() {
  // Get current date for defaults
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth(); // 0-11 (number, to match the numeric Select option values)
  const currentYear = currentDate.getFullYear();

  // Years from 2025 to current year
  const years = Array.from({ length: currentDate.getFullYear() - 2025 + 1 }, (_, i) => 2025 + i);

  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  
const pieChartRef = useRef(null);
const barChartRef = useRef(null);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // --- Fetch Departments ---
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await ReportService.getDepartments();
        const deptList = res.data || [];

        setDepartments(deptList);

        // Set first department as default
        if (deptList.length > 0) {
          setSelectedDepartment(deptList[0].id);
        }
      } catch (error) {
        console.error("Error fetching departments:", error);
        message.error("Failed to load departments.");
      }
    };
    fetchDepartments();
  }, []);

  // --- Fetch Attendance Data when filters change ---
  useEffect(() => {
    if (selectedDepartment && selectedMonth !== null && selectedYear) {
      fetchData(selectedDepartment, selectedMonth, selectedYear);
    }
  }, [selectedDepartment, selectedMonth, selectedYear]);

  const fetchData = async (departmentId, month, year) => {
    try {
      setLoading(true);

      // Convert month from 0-11 to 1-12 for backend
      const backendMonth = parseInt(month) + 1;
      const backendYear = parseInt(year);

      const res = await ReportService.getAttendanceReport(departmentId, backendMonth, backendYear);

      // Extract the records array from the response
      const responseData = res.data || {};
  
      const apiData = responseData.records || [];

      // Sort the data by date to ensure proper order
      const sortedData = apiData.sort(
        (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf()
      );

      if (sortedData.length > 0) {
        setData(sortedData);
        message.success(`Loaded ${sortedData.length} days for ${monthNames[month]} ${year}`);
      } else {
        setData([]);
        message.warning("No attendance records found for the selected period.");
      }
    } catch (error) {
      console.error("Error fetching attendance report:", error);
      console.error("Error details:", error.response?.data);
      message.error(
        `Failed to load attendance report: ${
          error.response?.data?.message || "Please check parameters"
        }`
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // --- PDF Export ---
  const handleDownloadPDF = () => {
    if (!data.length) return message.warning("No data to export");

    try {
      const doc = new jsPDF();

      // Title
      doc.setFontSize(16);
      doc.text("Employee Attendance Report", 105, 15, { align: "center" });

      // Department info
      const deptName =
        departments.find((d) => d.id === selectedDepartment)?.name || "Selected Department";

      doc.setFontSize(10);
      doc.text(`Department: ${deptName}`, 14, 25);

      // Period info
      const periodText = `Period: ${monthNames[selectedMonth]} ${selectedYear}`;
      doc.text(periodText, 14, 32);

      doc.text(`Generated: ${moment().format("MMMM DD, YYYY HH:mm")}`, 14, 39);
      doc.text(`Total Days: ${data.length}`, 14, 46);

      // Table data
      const tableData = data.map((day) => [
        moment(day.date).format("MMM-DD-YYYY"),
        day.isWeekend ? "Yes" : "No",
        day.isHoliday ? "Yes" : "No",
        day.totalPresent || 0,
        day.totalAbsent || 0,
        day.totalOnLeave || 0,
        day.totalLate || 0,
      ]);

      // Generate table
      doc.autoTable({
        head: [["Date", "Weekend", "Holiday", "Present", "Absent", "On Leave", "Late"]],
        body: tableData,
        startY: 55,
        styles: { fontSize: 8,
           cellPadding: 2 ,
           halign:"cneter",
           valign : "middle"
          },
        headStyles: { fillColor: [66, 133, 244], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          0: { cellWidth: 25 },
          1: { cellWidth: 25 },
          2: { cellWidth: 25 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25 },
          5: { cellWidth: 25 },
          6: { cellWidth: 20 },
        },
      });

      // Add summary
      const finalY = doc.lastAutoTable.finalY + 10;
      const totalDays = data.length;
      const totalPresent = data.reduce((sum, day) => sum + (day.totalPresent || 0), 0);
      const totalAbsent = data.reduce((sum, day) => sum + (day.totalAbsent || 0), 0);
      const workingDays = data.filter((day) => !day.isWeekend && !day.isHoliday).length;

      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text(`Summary: ${totalDays} total days (${workingDays} working days)`, 14, finalY);
      doc.text(`Total Present: ${totalPresent}, Total Absent: ${totalAbsent}`, 14, finalY + 7);

      const filename = `Attendance-Report-${deptName}-${monthNames[selectedMonth]}-${selectedYear}.pdf`;
      doc.save(filename);
      message.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("PDF generation error:", error);
      message.error("Failed to generate PDF");
    }
  };

  // Status mapping for better display
  const getStatusText = (status) => {
    const statusMap = {
      0: "Present",
      3: "Present",
      4: "Absent",
      5: "On Leave",
      6: "Weekend",
      7: "Holiday",
    };
    return statusMap[status] || "Unknown";
  };

  const getStatusColor = (status) => {
    const colorMap = {
      0: "green",
      3: "green",
      4: "red",
      5: "orange",
      6: "gold",
      7: "blue",
    };
    return colorMap[status] || "default";
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => moment(date).format("MMM-DD-YYYY"),
      sorter: (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf(),
    },
    {
      title: "Day Type",
      key: "dayType",
      render: (_, record) => {
        if (record.isHoliday) return <Tag color="blue">Holiday</Tag>;
        if (record.isWeekend) return <Tag color="gold">Weekend</Tag>;
        return <Tag color="green">Working Day</Tag>;
      },
      filters: [
        { text: "Working Day", value: "working" },
        { text: "Weekend", value: "weekend" },
        { text: "Holiday", value: "holiday" },
      ],
      onFilter: (value, record) => {
        if (value === "working") return !record.isWeekend && !record.isHoliday;
        if (value === "weekend") return record.isWeekend;
        if (value === "holiday") return record.isHoliday;
        return true;
      },
    },
    {
      title: "Present",
      dataIndex: "totalPresent",
      key: "totalPresent",
      sorter: (a, b) => (a.totalPresent || 0) - (b.totalPresent || 0),
      render: (value) => <Tag color={value > 0 ? "green" : "default"}>{value}</Tag>,
    },
    {
      title: "Absent",
      dataIndex: "totalAbsent",
      key: "totalAbsent",
      sorter: (a, b) => (a.totalAbsent || 0) - (b.totalAbsent || 0),
      render: (value) => <Tag color={value > 0 ? "red" : "default"}>{value}</Tag>,
    },
    {
      title: "On Leave",
      dataIndex: "totalOnLeave",
      key: "totalOnLeave",
      sorter: (a, b) => (a.totalOnLeave || 0) - (b.totalOnLeave || 0),
      render: (value) => <Tag color={value > 0 ? "orange" : "default"}>{value}</Tag>,
    },
    {
      title: "Late",
      dataIndex: "totalLate",
      key: "totalLate",
      sorter: (a, b) => (a.totalLate || 0) - (b.totalLate || 0),
      render: (value) => <Tag color={value > 0 ? "volcano" : "default"}>{value}</Tag>,
    },
    
  ];

  // Calculate summary statistics
  const getSummaryStats = () => {
    const totalDays = data.length;
    const workingDays = data.filter((day) => !day.isWeekend && !day.isHoliday).length;
    const weekends = data.filter((day) => day.isWeekend).length;
    const holidays = data.filter((day) => day.isHoliday).length;
    const totalPresent = data.reduce((sum, day) => sum + (day.totalPresent || 0), 0);
    const totalAbsent = data.reduce((sum, day) => sum + (day.totalAbsent || 0), 0);

    return { totalDays, workingDays, weekends, holidays, totalPresent, totalAbsent };
  };

  const stats = getSummaryStats();

// Compute chart data dynamically
const pieChartData = {
  labels: ["Present", "Absent", "On Leave", "Late"],
  datasets: [
    {
      data: [
        data.reduce((sum, day) => sum + (day.totalPresent || 0), 0),
        data.reduce((sum, day) => sum + (day.totalAbsent || 0), 0),
        data.reduce((sum, day) => sum + (day.totalOnLeave || 0), 0),
        data.reduce((sum, day) => sum + (day.totalLate || 0), 0),
      ],
      backgroundColor: ["#4CAF50", "#F44336", "#FF9800", "#9C27B0"],
      hoverOffset: 4,
    },
  ],
};

const pieOptions = {
  responsive: true,
  plugins: {
    legend: { position: "bottom" },
    tooltip: {
      callbacks: {
        label: function (tooltipItem) {
          return `${tooltipItem.label}: ${tooltipItem.raw}`;
        },
      },
    },
  },
};

const getBarChartData = () => {
  // labels = each date
  const labels = data.map(day => moment(day.date).format("DD MMM"));

  const presentData = data.map(day =>
    day.employees.filter(e => e.status === 0 || e.status === 3).length
  );

  const absentData = data.map(day =>
    day.employees.filter(e => e.status === 4).length
  );

  const onLeaveData = data.map(day =>
    day.employees.filter(e => e.status === 5).length
  );

  const lateData = data.map(day =>
    day.employees.filter(e => e.isLate).length
  );

  return {
    labels,
    datasets: [
      {
        label: "Present",
        data: presentData,
        backgroundColor: "#4CAF50",
      },
      {
        label: "Absent",
        data: absentData,
        backgroundColor: "#F44336",
      },
      {
        label: "On Leave",
        data: onLeaveData,
        backgroundColor: "#FF9800",
      },
      {
        label: "Late",
        data: lateData,
        backgroundColor: "#9C27B0",
      },
    ],
  };
};

const downloadPieChartPDF = () => {
  if (!pieChartRef.current) return;

  const chartImage = pieChartRef.current.toBase64Image();
  const doc = new jsPDF();

  doc.setFontSize(16);
  doc.text("Attendance Pie Chart", 105, 15, { align: "center" });
  doc.addImage(chartImage, "PNG", 15, 30, 180, 120); // adjust width/height

  doc.save(`Attendance-PieChart.pdf`);
};

const downloadBarChartPDF = () => {
  if (!barChartRef.current) return;

  const chartImage = barChartRef.current.toBase64Image();
  const doc = new jsPDF();

  doc.setFontSize(16);
  doc.text("Attendance Bar Chart", 105, 15, { align: "center" });
  doc.addImage(chartImage, "PNG", 15, 30, 180, 120);

  doc.save(`Attendance-BarChart.pdf`);
};

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        title="Employee Attendance Report"
        className="card-professional"
        extra={<Button onClick={() => window.history.back()}>Back</Button>}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Space size="middle">
              <Select
                value={selectedDepartment}
                style={{ width: 200 }}
                onChange={setSelectedDepartment}
                placeholder="Select Department"
                loading={departments.length === 0}
              >
                {departments.map((d) => (
                  <Option key={d.id} value={d.id}>
                    {d.name}
                  </Option>
                ))}
              </Select>

              <Select
                value={selectedMonth}
                onChange={setSelectedMonth}
                style={{ width: 180 }}
                placeholder="Select Month"
              >
                {monthNames.map((month, index) => (
                  <Option key={index} value={index}>
                    {month}
                  </Option>
                ))}
              </Select>

              <Select
                value={selectedYear}
                onChange={setSelectedYear}
                style={{ width: 120 }}
                placeholder="Select Year"
              >
                {years.map((year) => (
                  <Option key={year} value={year}>
                    {year}
                  </Option>
                ))}
              </Select>
            </Space>
          </Col>

          <Col>
            <Button
              className="btn-primary"
              icon={<FileDownloadOutlined />}
              onClick={handleDownloadPDF}
              disabled={!data.length}
            >
              Download PDF
            </Button>
          </Col>
        </Row>

        {/* Summary Statistics */}
        {data.length > 0 && (
          <div style={{ marginBottom: 16, padding: 16, background: "#f5f5f5", borderRadius: 6 }}>
            <Row gutter={16}>
              <Col>
                <Tag color="blue">Total Days: {stats.totalDays}</Tag>
              </Col>
              <Col>
                <Tag color="green">Working Days: {stats.workingDays}</Tag>
              </Col>
              <Col>
                <Tag color="gold">Weekends: {stats.weekends}</Tag>
              </Col>
              <Col>
                <Tag color="blue">Holidays: {stats.holidays}</Tag>
              </Col>
              <Col>
                <Tag color="green">Total Present: {stats.totalPresent}</Tag>
              </Col>
              <Col>
                <Tag color="red">Total Absent: {stats.totalAbsent}</Tag>
              </Col>
            </Row>
          </div>
        )}

        <div style={{ marginBottom: 16, color: "#666" }}>
          {selectedDepartment && selectedMonth !== null && selectedYear
            ? `Showing ${data.length} days for ${monthNames[selectedMonth]} ${selectedYear}`
            : "Please select department, month and year"}
        </div>

{data.length > 0 && (
  <Card
    title="Attendance Pie Chart"
    style={{ maxWidth: 400, margin: "0 auto", marginBottom: 16 }}
    extra={
      <Button size="small" onClick={downloadPieChartPDF}>
        Download PDF
      </Button>
    }
  >
    <div style={{ width: 300, height: 300, margin: "0 auto" }}>
      <Pie ref={pieChartRef} data={pieChartData} options={pieOptions} />
    </div>
  </Card>
)}

{data.length > 0 && (
  <Card
    title="Attendance Bar Chart"
    style={{ maxWidth: 700, margin: "0 auto", marginBottom: 16 }}
    extra={
      <Button size="small" onClick={downloadBarChartPDF}>
        Download PDF
      </Button>
    }
  >
    <div style={{ overflowX: "auto" }}>
      <Bar
        ref={barChartRef}
        data={getBarChartData()}
        options={{
          responsive: true,
          plugins: {
            legend: { position: "top" },
            title: { display: true, text: "Attendance per Day" },
          },
          scales: {
            x: { stacked: true, ticks: { maxRotation: 45, minRotation: 30 } },
            y: { stacked: true, beginAtZero: true },
          },
        }}
        height={100} // adjust as needed
      />
    </div>
  </Card>
)}

        <Table
          columns={columns}
          dataSource={data}
          rowKey={(record) => record.date}
          loading={loading}
          pagination={{
            pageSize: 31,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} days`,
          }}
          scroll={{ x: 1200 }}
          expandable={{
            expandedRowRender: (record) => {
              if (!record.employees || record.employees.length === 0) {
                return (
                  <div style={{ padding: 16, textAlign: "center", color: "#999" }}>
                    No user details available for this day
                  </div>
                );
              }

              return (
                <div style={{ margin: 0 }}>
                  <h4>
                    Detailed User Information for {moment(record.date).format("YYYY-MM-DD (dddd)")}
                  </h4>
                  <Table
                    size="small"
                    dataSource={record.employees || []}
                    rowKey={(user) => `${user.userId}-${user.id}`}
                    pagination={false}
                    columns={[
                      {
                        title: "Status",
                        dataIndex: "status",
                        key: "status",
                        render: (status) => (
                          <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
                        ),
                      },
                      {
                        title: "Late",
                        dataIndex: "isLate",
                        key: "isLate",
                        render: (val) => (val ? <Tag color="volcano">Yes</Tag> : "No"),
                      },
                      {
                        title: "Early Leave",
                        dataIndex: "isEarlyLeave",
                        key: "isEarlyLeave",
                        render: (val) => (val ? <Tag color="orange">Yes</Tag> : "No"),
                      },
                      {
                        title: "Worked Hours",
                        dataIndex: "totalWorkedHours",
                        key: "totalWorkedHours",
                        render: (hours) =>
                          hours && hours !== "00:00:00" ? hours.split(".")[0] : "00:00:00",
                      },
                    ]}
                  />
                </div>
              );
            },
            rowExpandable: (record) => record.employees && record.employees.length > 0,
          }}
        />
      </Card>
      <Footer />
    </DashboardLayout>
  );
}

export default EmployeeAttendanceReport;
